/* eslint-disable no-alert */
import React from 'react';
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useQuery, useMutation, useParameterizedQuery } from 'react-fetching-library';
import SendIcon from '@mui/icons-material/Send';
import { getFormSectionsAction, submitFormAction, getHistoricDataByPeselAction, getFormMetaDataAction } from '../../services/request';
import { SectionPanel } from './SectionPanel';
import { createYupSchema, formatDateTime, formatDate, numberToPercent, getOptionIndex, addDays } from '../../services/functions';
import { useFullScreen, useDynamicInitAction, useDynamicQuery, useDidMountEffect } from '../../services/hooks';
import FormSentInfo from './FormSentInfo';

export const FormPanel = props => {
  const fullScreen = useFullScreen();
  const { form, clientData, participantData, setIsHiddenSelectParticipant } = props;
  const { loading, payload, error } = useQuery(getFormSectionsAction(form.id));
  const { loading: metaLoading, payload: metaPayload, error: metaError } = useQuery(getFormMetaDataAction(form.id));
  const submitForm = useMutation(submitFormAction);
  const initAction = useDynamicInitAction(form.init_function);
  const initData = useDynamicQuery(initAction);
  const historicPeselAction = useParameterizedQuery(getHistoricDataByPeselAction);
  const [sections, setSections] = React.useState([]);
  const [prevSections, setPrevSections] = React.useState([]);
  const [fields, setFields] = React.useState([]);
  const [today, setToday] = React.useState(new Date());
  const [isFormSent, setIsFormSent] = React.useState(false);
  const [metaData, setMetaData] = React.useState([]);
  const [showErrors, setShowErrors] = React.useState(false);

  React.useEffect(() => {
    if (!error && !loading) {
      if (!_.isNil(form.init_function) && !_.isEmpty(initData.data)) {
        const sectionsToConst = _.cloneDeep(payload);
        _.map(initData.data, (options, key) => {
          _.map(sectionsToConst, section => {
            const index = sectionsToConst.indexOf(section);
            sectionsToConst.splice(index, 1);
            _.map(section.fields, field => {
              if (field.name === key) {
                field.dynamicOptions = options;
              }
            });
            sectionsToConst.splice(index, 0, section);
          });
        });
        setSections(sectionsToConst);
        const fetchedFields = [];
        _.map(sectionsToConst, section => _.map(section.fields, obj => fetchedFields.push(obj)));
        setFields(fetchedFields);
      } else {
        setSections(payload);
        const fetchedFields = [];
        _.map(payload, section => _.map(section.fields, obj => fetchedFields.push(obj)));
        setFields(fetchedFields);
      }
    }
  }, [loading]);

  React.useEffect(() => {
    if (!metaError && !metaLoading) {
      setMetaData(metaPayload.actions.GET);
    }
  }, [metaLoading]);

  const yupSchema = fields.reduce(createYupSchema, {});
  const validationSchema = Yup.object().shape(yupSchema);
  const initialValues = {};
  _.map(fields, obj => {
    if (obj.type === 'datetime') {
      initialValues[obj.name] = formatDateTime(today);
    } else if (obj.type === 'date') {

      if (_.isNil(participantData) || !_.isObject(participantData) || !obj.name.startsWith('birth_date')) {
        initialValues[obj.name] = null;
      } else if (participantData.hasOwnProperty(obj.name) && !_.isNil(participantData[obj.name])) {
        initialValues[obj.name] = formatDate(participantData[obj.name]);
      } else {
        initialValues[obj.name] = null;
      }

    } else if (obj.type === 'time') {
      initialValues[obj.name] = today;
    } else if (obj.type === 'bool') {
      initialValues[obj.name] = false;
    } else if (obj.type === 'multichoice') {
      initialValues[obj.name] = [];
    } else if (obj.type === 'multichar') {
      initialValues[obj.name] = [{}];
    } else if (obj.type === 'radiochoice') {
      if (!obj.name.startsWith('participant_gender') || _.isNil(participantData) || _.isEmpty(clientData)) {
        initialValues[obj.name] = obj.options[0];
      } else if (participantData.hasOwnProperty('gender') && !_.isNil(participantData.gender)) {

        if (participantData.gender === 'boy') {
          initialValues[obj.name] = obj.options[0];
        } else {
          initialValues[obj.name] = obj.options[1];
        }

      } else {
        initialValues[obj.name] = obj.options[0];
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (obj.name.startsWith('guardian_') && !_.isNil(clientData) && !_.isEmpty(clientData)) {
        const key = obj.name.replace('guardian_', '');
        if (clientData.hasOwnProperty(key) && !_.isNil(clientData[key])) {
          initialValues[obj.name] = clientData[key];
        } else {
          initialValues[obj.name] = '';
        }
      } else if (obj.name.startsWith('participant_') && !_.isNil(participantData) && !_.isEmpty(participantData)) {
        const key = obj.name.replace('participant_', '');
        if (participantData.hasOwnProperty(key) && !_.isNil(participantData[key])) {
          initialValues[obj.name] = participantData[key];
        } else {
          initialValues[obj.name] = '';
        }
      } else {
        initialValues[obj.name] = '';
      }
    }
  });

  const handleSubmit = async values => {
    console.log(values);
    console.log(formik.validate);
    const { error, payload } = await submitForm.mutate(values, form.id);
    console.log(payload);
    if (!error && payload.success === true) {
      setIsFormSent(true);
      if (_.isFunction(setIsHiddenSelectParticipant)) {
        setIsHiddenSelectParticipant(true);
      }
    } else {
      alert(payload.message);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnChange: true, // wyłączyć to w razie gdyby za wolno chodził formularz
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  const updateOptionsCallback = data => {
    const sectionsToConst = _.cloneDeep(sections);
    _.map(sectionsToConst, section => {
      const index = sectionsToConst.indexOf(section);
      sectionsToConst.splice(index, 1);
      _.map(data, (options, key) => {
        if (key.includes('_id')) {
          setFieldById(options, key);
        } else {
          _.map(section.fields, field => {
            if (field.name === key) {
              if (field.type == 'multichoice') {
                formik.setFieldValue(key, []);
              } else if (field.name == 'discount') {
                formik.setFieldValue(key, [null, null, null, null, null]);

              } else {
                formik.setFieldValue(key, '');
              }
              field.dynamicOptions = options;
            }
          });
        }
      });
      sectionsToConst.splice(index, 0, section);

    });
    setSections(sectionsToConst);
  };

  const setFieldById = (val, key) => {
    const field_name = key.split('_')[0];
    formik.setFieldValue(field_name, val);
  };

  const yupRequiredFieldsCallback = (params, sectionId, optionIndex, newVal = null) => {
    // no póki co dla jednego parametru to robimy, nie wiem czy będzie więcej. Ta funkcja obsługuje choicefield, booleany i radiofieldy z dwoma opcjami
    // dla radiofield więcej niż jeden parametr
    // choice fieldy z opcją exists
    const fieldsToConst = _.cloneDeep(fields);
    const sectionsToConst = _.cloneDeep(sections);
    const section = _.filter(sectionsToConst, section => section.id === sectionId)[0];
    const index = sectionsToConst.indexOf(section);
    sectionsToConst.splice(index, 1);
    _.map(section.fields, field => {
      const yupField = _.find(fieldsToConst, obj => obj.id === field.id);
      const requiredObject = { type: 'required', params: ['To pole jest wymagane'] };
      const oneOf = {
        type: 'oneOf',
        params: [[true], 'Należy zaakceptować warunki']
      };

      const isFound = () => {
        if (!field.show_on || !_.isArray(params)) {
          return false;
        }

        const args = field.show_on.trim().split(' ')
          .map(show_on => show_on.replace(/\s/g, ''));

        return args.some(arg => params.includes(arg));
      };
      if (field.show_on && field.show_on.includes('exists') && isFound()) {
        // Sprawdzamy czy atrybut nadrzędny posiada jakąś wartość, jeśli nie to znaczy że pole nie jest wymagane
        if (newVal) {
          field.required = true;
          if (field.type == 'bool') {
            yupField.validations.push(oneOf);
          } else {
            yupField.validations.push(requiredObject);
          }
        } else {
          field.required = false;
          if (field.type == 'bool') {
            yupField.validations = _.filter(yupField.validations, obj => obj.type !== 'oneOf');
          } else {
            yupField.validations = _.filter(yupField.validations, obj => obj.type !== 'required');
          }
        }
      } else if (field.show_on && isFound() && !field.lock_required) {
        const args = field.show_on.trim().split(' ')
          .map(show_on => show_on.replace(/\s/g, ''));

        for (const arg of args) {
          const index = params.indexOf(arg);
          if (optionIndex == getOptionIndex(params[index])) {
            field.required = true;
            yupField.validations.push(requiredObject);
            break;
          } else {
            field.required = false;
            const vals = yupField.validations;
            yupField.validations = _.filter(vals, obj => obj.type !== 'required');
          }
        }

      } else if (field.show_on && field.show_on.includes('false')) {
        if (params) {
          field.required = false;
          const vals = yupField.validations;
          yupField.validations = _.filter(vals, obj => obj.type !== 'required');
        } else {
          field.required = true;
          yupField.validations.push(requiredObject);
        }
      } else if (field.show_on && field.show_on.includes('true')) {
        if (!params) {
          field.required = false;
          const vals = yupField.validations;
          yupField.validations = _.filter(vals, obj => obj.type !== 'required');
        } else {
          field.required = true;
          yupField.validations.push(requiredObject);
        }
      }
    });
    sectionsToConst.splice(index, 0, section);
    setSections(sectionsToConst);
    setFields(fieldsToConst);

    return sectionsToConst;
  };

  const bigFamilyCardCallback = (params, sectionId, optionIndex) => {
    yupRequiredFieldsCallback(params, sectionId, optionIndex);
    // if (optionIndex == getOptionIndex(params[0])) {
    //   setPrevSections(sections);
    //   const sectionsToConst = _.cloneDeep(sections);
    //   const section = _.filter(sectionsToConst, section => section.id === sectionId)[0];
    //   const index = sectionsToConst.indexOf(section);
    //   sectionsToConst.splice(index, 1);
    //   _.map(section.fields, field => {
    //     if (field.name === 'discount') {
    //       field.dynamicOptions = _.filter(field.dynamicOptions, option => option.is_connected_with_big_family_card);
    //     }
    //   });
    //   sectionsToConst.splice(index, 0, section);
    //   setSections(sectionsToConst);
    // } else {
    //   setSections(prevSections);
    // }
  };

  const updateDiscountsCallback = (discount, sectionId, discountArr, promId = null) => {
    if (promId) {
      return _.filter(metaData.promotions, prom => prom.id == promId)[0];
    }
    const sectionsToConst = _.cloneDeep(sections);
    const section = _.filter(sectionsToConst, section => section.id === sectionId)[0];
    const index = sectionsToConst.indexOf(section);
    sectionsToConst.splice(index, 1);
    _.map(section.fields, field => {
      if (field.name === 'discount') {
        field.dynamicOptions = [...field.dynamicOptions, discount];
      }
    });
    sectionsToConst.splice(index, 0, section);
    setSections(sectionsToConst);
    handleDiscountActions(discountArr, sectionId, sectionsToConst);
  };

  const historicDataByPeselCallback = async pesel => {
    const { loading, payload, error } = await historicPeselAction.query(pesel);
    if (!loading, !error) {
      if (payload.count == 0) {
        return;
      }
      // eslint-disable-next-line no-alert
      const result = confirm(`Znaleziono ${payload.count} zamówień z podanym numerem pesel. Czy wczytać dane?`);
      if (result == true) {
        _.map(payload, (value, key) => {
          formik.setFieldValue(key, value);
        });
      };
    }
  };

  const checkActivitiesCallback = (action, item, field, currentValues) => {
    const period = sections[0].fields.find(x => x.name == 'period').dynamicOptions.find(x => x.id == formik.values.period);
    if (form.version.toString().startsWith('2024')) {
      // Sezon 2024:
      const mainActivitiesMax = period.main_activities_count;
      const ropeActivitiesMax = period.rope_activities_count;
      const educationalActivitiesMax = period.educational_activities_count;
      const entertainmentActivitiesMax = period.entertainment_activities_count;
      const integrationActivitiesMax = period.integration_activities_count;
      const summaryMax = mainActivitiesMax;
      let currentMainActivities = 0;
      let currentRopeActivities = 0;
      let currentEducationalActivities = 0;
      let currentEntertainmentActivities = 0;
      let currentIntegrationActivities = 0;
      // eslint-disable-next-line guard-for-in
      for (const idx in currentValues) {
        const obj = field.dynamicOptions.find(x => x.id == currentValues[idx]);
        if (obj.type == 'rope_activity') {
          currentRopeActivities += 1;
        } else if (obj.type == 'educational') {
          currentEducationalActivities += 1;
        } else if (obj.type == 'entertainment') {
          currentEntertainmentActivities += 1;
        } else if (obj.type == 'integration') {
          currentIntegrationActivities += 1;
        } else {
          currentMainActivities += 1;
        }
      }
      if (action == 'add') {
        if (currentMainActivities + currentRopeActivities + currentEducationalActivities + currentEntertainmentActivities + currentIntegrationActivities >= summaryMax) {
          return { error: true, message: `Proszę wybrać maksymalnie ${currentMainActivities + currentRopeActivities + currentEducationalActivities + currentEntertainmentActivities + currentIntegrationActivities} zajęć / ${summaryMax} zajęć`, currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, currentEntertainmentActivities, entertainmentActivitiesMax, currentIntegrationActivities, integrationActivitiesMax };
        }
        if (currentRopeActivities >= ropeActivitiesMax && item.type == 'rope_activity') {
          return { error: true, message: `Prosimy wybrać maksymalnie ${currentRopeActivities}/${ropeActivitiesMax} zajęcie linowe`, currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, currentEntertainmentActivities, entertainmentActivitiesMax, currentIntegrationActivities, integrationActivitiesMax };
        }
        if (currentEducationalActivities >= educationalActivitiesMax && item.type == 'educational') {
          return { error: true, message: `Prosimy wybrać maksymalnie ${currentEducationalActivities}/${educationalActivitiesMax} zajęć edukacyjnych`, currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, currentEntertainmentActivities, entertainmentActivitiesMax, currentIntegrationActivities, integrationActivitiesMax };
        }
        if (currentEntertainmentActivities >= entertainmentActivitiesMax && item.type == 'entertainment') {
          return { error: true, message: `Prosimy wybrać maksymalnie ${currentEntertainmentActivities}/${entertainmentActivitiesMax} zajęć rozrywkowych`, currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, currentEntertainmentActivities, entertainmentActivitiesMax, currentIntegrationActivities, integrationActivitiesMax };
        }
        if (currentIntegrationActivities >= integrationActivitiesMax && item.type == 'integration') {
          return { error: true, message: `Prosimy wybrać maksymalnie ${currentIntegrationActivities}/${integrationActivitiesMax} zajęć integracyjnych`, currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, currentEntertainmentActivities, entertainmentActivitiesMax, currentIntegrationActivities, integrationActivitiesMax };
        }
        if (item.type == 'rope_activity') {
          currentRopeActivities += 1;
        } else if (item.type == 'educational') {
          currentEducationalActivities += 1;
        } else if (item.type == 'entertainment') {
          currentEntertainmentActivities += 1;
        } else if (item.type == 'integration') {
          currentIntegrationActivities += 1;
        } else {
          currentMainActivities += 1;
        }

        return { error: false, message: '', currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, currentEntertainmentActivities, entertainmentActivitiesMax, currentIntegrationActivities, integrationActivitiesMax };
      }
      if (item.type == 'rope_activity') {
        currentRopeActivities -= 1;
      } else if (item.type == 'educational') {
        currentEducationalActivities += 1;
      } else if (item.type == 'entertainment') {
        currentEntertainmentActivities -= 1;
      } else if (item.type == 'integration') {
        currentIntegrationActivities -= 1;
      } else {
        currentMainActivities -= 1;
      }

      return { currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, currentEntertainmentActivities, entertainmentActivitiesMax, currentIntegrationActivities, integrationActivitiesMax };
    }
    // Sezon 2023:
    const mainActivitiesMax = period.main_activities_count;
    const ropeActivitiesMax = period.rope_activities_count;
    const educationalActivitiesMax = period.educational_activities_count;
    const summaryMax = mainActivitiesMax + ropeActivitiesMax + educationalActivitiesMax;
    let currentMainActivities = 0;
    let currentRopeActivities = 0;
    let currentEducationalActivities = 0;
    let artisticActivityIsPresent = 0;
    // eslint-disable-next-line guard-for-in
    for (const idx in currentValues) {
      const obj = field.dynamicOptions.find(x => x.id == currentValues[idx]);
      if (obj.type == 'rope_activity') {
        currentRopeActivities += 1;
      } else if (obj.type == 'educational') {
        currentEducationalActivities += 1;
      } else if (obj.type == 'artistic') {
        artisticActivityIsPresent = 1;
      } else {
        currentMainActivities += 1;
      }
    }
    if (action == 'add') {
      if (currentMainActivities + currentRopeActivities + currentEducationalActivities + artisticActivityIsPresent >= summaryMax) {
        return { error: true, message: `Proszę wybrać maksymalnie ${currentMainActivities + currentRopeActivities + currentEducationalActivities + artisticActivityIsPresent} zajęć / ${summaryMax} zajęć`, currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, artisticActivityIsPresent };
      }
      if (currentRopeActivities >= ropeActivitiesMax && item.type == 'rope_activity') {
        return { error: true, message: `Prosimy wybrać maksymalnie ${currentRopeActivities}/${ropeActivitiesMax} zajęcie linowe`, currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, artisticActivityIsPresent };
      }
      if (currentEducationalActivities >= educationalActivitiesMax && item.type == 'educational') {
        return { error: true, message: `Prosimy wybrać maksymalnie ${currentEducationalActivities}/${educationalActivitiesMax} zajęć edukacyjnych`, currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, artisticActivityIsPresent };
      }
      if (artisticActivityIsPresent > 0 && item.type == 'artistic') {
        return { error: true, message: 'Wybrano już jedno zajęcie artystyczne', currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, artisticActivityIsPresent };
      }

      if (item.type == 'rope_activity') {
        currentRopeActivities += 1;
      } else if (item.type == 'educational') {
        currentEducationalActivities += 1;
      } else if (item.type == 'artistic') {
        artisticActivityIsPresent = 1;
      } else {
        currentMainActivities += 1;
      }

      return { error: false, message: '', currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, artisticActivityIsPresent };
    }
    if (item.type == 'rope_activity') {
      currentRopeActivities -= 1;
    } else if (item.type == 'educational') {
      currentEducationalActivities += 1;
    } else if (item.type == 'artistic') {
      artisticActivityIsPresent = 0;
    } else {
      currentMainActivities -= 1;
    }

    return { currentMainActivities, mainActivitiesMax, currentRopeActivities, ropeActivitiesMax, currentEducationalActivities, educationalActivitiesMax, artisticActivityIsPresent };
  };

  const setDateToCallback = date => {
    const period = sections[0].fields.find(x => x.name == 'period').dynamicOptions.find(x => x.id == formik.values.period);
    if (period.is_weekend && date.getDay() !== 5) {
      alert('Wybrano termin weekendowy. Należy zaznaczyć piątek.');
      formik.setFieldValue('period_to', null);
    } else if (period.days_count == 3 && (date.getDay() !== 1 && date.getDay() !== 3 && date.getDay() !== 5)) {
      alert('Wycieczki 3-dniowe zaczynają się od poniedziałku, środy bądź piątku.');
      formik.setFieldValue('period_to', null);
    } else if (period.days_count == 3 && date.getDay() >= 1 && date.getMonth() == 9) {
      alert('Proszę wybrać datę minimalnie o 3 dni mniejszą od 4 września');
      formik.setFieldValue('period_to', null);
    } else if (period.days_count == 5 && ((date.getDay() >= 30 && date.getMonth() == 8) || (date.getDay() >= 1 && date.getMonth() == 9))) {
      alert('Proszę wybrać datę minimalnie o 5 dni mniejszą od 4 września');
      formik.setFieldValue('period_to', null);
    } else if (period.days_count == 5 && date.getDay() >= 18 && date.getMonth() == 5) {
      alert('Proszę wybrać datę minimalnie o 3 dni mniejszą od 21 czerwca');
      formik.setFieldValue('period_to', null);
    } else if (period.days_count == 5 && date.getDay() >= 16 && date.getMonth() == 5) {
      alert('Proszę wybrać datę minimalnie o 5 dni mniejszą od 21 czerwca');
      formik.setFieldValue('period_to', null);
    } else {
      formik.setFieldValue('period_from', formatDate(date));
      formik.setFieldValue('period_to', formatDate(addDays(date, period.days_count - 1)));
    }
  };

  const hideFieldCallback = (fieldName, sectionId, reverse, required = false) => {
    const sectionsToConst = _.cloneDeep(sections);
    const fieldsToConst = _.cloneDeep(fields);
    const section = _.filter(sectionsToConst, section => section.id === sectionId)[0];
    const index = sectionsToConst.indexOf(section);
    sectionsToConst.splice(index, 1);
    _.map(section.fields, field => {
      if (field.name === fieldName) {
        const yupField = _.find(fieldsToConst, obj => obj.id === field.id);
        if (reverse) {
          yupField.hide = false;
          yupField.required = required;
        } else {
          yupField.hide = true;
          yupField.required = required;
          formik.setFieldValue(fieldName, '');
        }
        if (required) {
          yupField.validations.push({ type: 'required', params: ['To pole jest wymagane'] });
        } else {
          yupField.validations = _.filter(field.validations, obj => obj.type !== 'required');
        }
        field.hide = yupField.hide;
        field.required = yupField.required;
        field.validations = yupField.validations;
      }
    });
    sectionsToConst.splice(index, 0, section);
    setSections(sectionsToConst);
    setFields(fieldsToConst);
  };

  const hideFieldsCallback = (sectionId, toHideFieldNames = [], toShowFieldNames = [], newSections = null) => {
    let sectionsToConst = _.cloneDeep(sections);
    if (newSections) {
      sectionsToConst = newSections;
    }
    const section = _.filter(sectionsToConst, section => section.id === sectionId)[0];
    const fieldsToConst = _.cloneDeep(fields);
    const index = sectionsToConst.indexOf(section);
    sectionsToConst.splice(index, 1);
    _.map(toHideFieldNames, fieldName => {
      _.map(section.fields, field => {
        if (field.name === fieldName) {
          field.hide = true;
          if (fieldName == 'friends' || fieldName == 'siblings') {
            formik.setFieldValue(fieldName, [{}]);
          } else if (fieldName == 'friends_promotion_choice') {
            formik.setFieldValue(fieldName, 'Chcę otrzymać rabat');
          } else {
            formik.setFieldValue(fieldName, '');
          }
          const yupField = _.find(fieldsToConst, obj => obj.id === field.id);

          yupField.required = false;
          yupField.validations = _.filter(field.validations, obj => obj.type !== 'required');

          field.required = yupField.required;
          field.validations = yupField.validations;
        }
      });
    });
    _.map(toShowFieldNames, fieldName => {
      _.map(section.fields, field => {
        if (field.name === fieldName) {
          field.hide = false;
          const yupField = _.find(fieldsToConst, obj => obj.id === field.id);

          yupField.required = true;
          yupField.validations.push({ type: 'required', params: ['To pole jest wymagane'] });

          field.required = yupField.required;
          field.validations = yupField.validations;
        }
      });
    });
    sectionsToConst.splice(index, 0, section);
    setSections(sectionsToConst);
    setFields(fieldsToConst);
  };

  const handleDiscountActions = (ids, sectionId, sections) => {
    const toHideFieldNames = [];
    const toShowFieldNames = [];
    const proms = _.filter(metaData.promotions, prom => ids.includes(prom.id.toString()) || ids.includes(prom.id));
    if (_.isEmpty(proms)) {
      toHideFieldNames.push('friends', 'friends_promotion_choice', 'siblings', 'has_big_family_card');
      hideFieldsCallback(sectionId, toHideFieldNames, toShowFieldNames, sections);

      return;
    }
    if (proms.some(prom => prom.for_friend)) {
      toShowFieldNames.push('friends', 'friends_promotion_choice');
    } else {
      toHideFieldNames.push('friends', 'friends_promotion_choice');
    }
    const connectedProms = _.filter(proms, prom => prom.is_connected_with_big_family_card);
    const showBigFamilyCard = connectedProms.length === proms.length;
    if (showBigFamilyCard && !formik.values.boskie_wakacje) {
      toShowFieldNames.push('has_big_family_card');
    } else {
      toHideFieldNames.push('has_big_family_card');
    }
    if (proms.some(prom => prom.for_siblings)) {
      toShowFieldNames.push('siblings');
    } else {
      toHideFieldNames.push('siblings');
    }
    hideFieldsCallback(sectionId, toHideFieldNames, toShowFieldNames, sections);
  };

  const handleParishChange = (id, sectionId) => {
    const parish = _.filter(metaData.parishes, p => id == p.id)[0];
    if (parish && parish.name.includes('Inne')) {
      hideFieldCallback('parish_other', sectionId, true, true);
    } else {
      hideFieldCallback('parish_other', sectionId, false, false);
    }
  };

  const handleGroupChange = (id, sectionId, sections) => {
    const adultsFields = ['job_or_scholl_address', 'health_statement', 'payment_regulations', 'personal_survey', 'statement'];
    const kidsFields = ['participant_name', 'participant_pesel', 'participant_address', 'job_or_scholl_address_kids', 'health_statement_kids', 'payment_regulations_kids', 'personal_survey_kids', 'statement_kids'];
    if (metaData.groups_kids.includes(id)) {
      hideFieldsCallback(sectionId, adultsFields, kidsFields, sections);
    } else {
      hideFieldsCallback(sectionId, kidsFields, adultsFields, sections);
    }
  };

  const handleEventChange = (val, sectionId) => {
    if (form.identifier === 'polkolonie') {
      const event = _.filter(metaData.events, event => event.id == val)[0];
      if (event && event.canoeing_activities) {
        hideFieldCallback('canoeing_activities', sectionId, true, true);
      } else {
        hideFieldCallback('canoeing_activities', sectionId, false, false);
      }
    }
  };

  const handlePeriodChange = (v, sectionId, sections) => {
    if (v.props.id.includes('1 DZIEŃ') || v.props.id.includes('1-DNIOWE')) {
      hideFieldsCallback(sectionId, ['educational_trip', 'paintball_participants_count'], [], sections);
    } else {
      hideFieldsCallback(sectionId, [], ['educational_trip', 'paintball_participants_count'], sections);
    }
  };

  const handleSourceOfOfferChange = (obj, sectionId) => {
    if (form.version.toString().startsWith('2024')) {
      if (obj.name.toLowerCase().includes('jestem stałym klientem')) {
        hideFieldCallback('source_of_offer_additional_info', sectionId, true, true);
      } else {
        hideFieldCallback('source_of_offer_additional_info', sectionId, false, false);
      }
    }
  };

  useDidMountEffect(() => {
    setShowErrors(true);
  }, [formik.isSubmitting]);

  if (_.isEmpty(formik.values)) {
    return null;
  }

  return (
    <Card elevation={4} sx={{ opacity: '0.95', mt: 2 }}>
      <CardContent>
        {isFormSent
          ? <FormSentInfo formId={form.id} identifier={form.identifier} />
          : <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' }}>
            {_.map(sections, (obj, idx) => (
              <Box style={{ flex: !fullScreen ? numberToPercent(obj.flex) : '100%' }} key={idx}>
                <SectionPanel
                  section={obj}
                  formik={formik}
                  formVersion={form.version}
                  updateOptionsCallback={updateOptionsCallback}
                  yupRequiredFieldsCallback={yupRequiredFieldsCallback}
                  bigFamilyCardCallback={bigFamilyCardCallback}
                  updateDiscountsCallback={updateDiscountsCallback}
                  historicDataByPeselCallback={historicDataByPeselCallback}
                  checkActivitiesCallback={checkActivitiesCallback}
                  setDateToCallback={setDateToCallback}
                  hideFieldCallback={hideFieldCallback}
                  handleDiscountActions={handleDiscountActions}
                  handleParishChange={handleParishChange}
                  handleGroupChange={handleGroupChange}
                  handleEventChange={handleEventChange}
                  handlePeriodChange={handlePeriodChange}
                  handleSourceOfOfferChange={handleSourceOfOfferChange} />
              </Box>
            ))}
            <Box textAlign="center">
              <Button
                color="primary"
                variant="contained"
                type="submit"
                style={{ marginBottom: 20, marginTop: 20 }}
                endIcon={<SendIcon />}
                disabled={submitForm.loading}
              >
          Zatwierdź i wyślij
              </Button>
              {/* { showErrors
                ? <>
                  {_.map(formik.errors, (v, k) => (
                    <span key={k}>
                      <Typography variant="body1" color="error"><b>{_.filter(fields, field => field.name == k)[0].label} - <u>{v}</u></b></Typography>
                    </span>
                  ))}
                </> : null
              } */}
            </Box>
          </form>}

      </CardContent>
    </Card>
  );
};
